import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Lock, Eye, EyeOff, Save, Trash2, History, Copy, ShieldAlert } from 'lucide-react';
import { colors, API, authHeaders, fmt } from '../utils/theme';
import { PageHeader, Badge, Button, LoadingSpinner, EmptyState } from '../components/UI';
import useIsMobile from '../hooks/useIsMobile';

// Cofre de dados dos cartões (14/09/2026): número, validade e CVV cifrados no
// servidor. Aqui só se vê a máscara; "Revelar" busca os segredos (auditado) e
// esconde sozinho depois de 60 s. Nunca guardamos nada no navegador.
async function api(path, opts = {}) {
  const r = await fetch(`${API}/cofre${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
}
const inp = { padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: 14, boxSizing: 'border-box', width: '100%' };
const thS = { padding: '8px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdS = { padding: '8px 10px', color: colors.text, fontSize: 13, verticalAlign: 'middle' };
const fmtNum = (n) => String(n || '').replace(/(\d{4})(?=\d)/g, '$1 ');

function LinhaCartao({ c, podeEditar, onChange }) {
  const [revelado, setRevelado] = useState(null);
  const [editando, setEditando] = useState(false);
  // `mesmo_cpf`/`mesmo_endereco` marcados por padrão: o normal é o cartão estar
  // no nome e no endereço do próprio titular. Quem copia é o servidor.
  const VAZIO = {
    numero: '', validade: '', cvv: '', nome_impresso: c.nome_impresso || '',
    mesmo_cpf: true, cpf_titular_cartao: '',
    mesmo_endereco: true, cobranca_cep: '', cobranca_logradouro: '', cobranca_numero: '',
    cobranca_complemento: '', cobranca_bairro: '', cobranca_cidade: '', cobranca_uf: '',
  };
  const [form, setForm] = useState(VAZIO);
  const [busy, setBusy] = useState(false);
  const mobile = useIsMobile();
  useEffect(() => {
    if (!revelado) return undefined;
    const t = setTimeout(() => setRevelado(null), 60000);
    return () => clearTimeout(t);
  }, [revelado]);
  const revelar = async () => {
    setBusy(true);
    try { setRevelado(await api(`/cartoes/${c.id}/revelar`, { method: 'POST' })); } catch (e) { alert(e.message); }
    setBusy(false);
  };
  const salvar = async () => {
    setBusy(true);
    try {
      await api(`/cartoes/${c.id}`, { method: 'PUT', body: JSON.stringify(form) });
      setEditando(false); setForm({ ...VAZIO, nome_impresso: form.nome_impresso }); setRevelado(null); onChange();
    } catch (e) { alert(e.message); }
    setBusy(false);
  };
  const apagar = async () => {
    if (!window.confirm(`Apagar os dados do cofre de ${c.nome_cartao}?`)) return;
    try { await api(`/cartoes/${c.id}`, { method: 'DELETE' }); setRevelado(null); onChange(); } catch (e) { alert(e.message); }
  };
  const copiar = (txt) => navigator.clipboard?.writeText(txt).catch(() => {});
  return (
    <>
      <tr style={{ borderBottom: `1px solid ${colors.border}22` }}>
        <td style={tdS}>
          <div style={{ fontWeight: 600 }}>{c.nome_cartao}</div>
          <div style={{ fontSize: 11, color: colors.textDim }}>{[c.titular_nome, c.banco, c.bandeira, c.final && `final ${c.final}`].filter(Boolean).join(' · ')}</div>
          {/* O cartão inteiro numa linha: quem vai comprar não precisa abrir a
              tela de cartões pra saber quando vence ou quanto tem de limite. */}
          <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
            {[c.vencimento && `vence dia ${String(c.vencimento).slice(-2)}`,
              c.dia_fechamento && `fecha dia ${c.dia_fechamento}`,
              c.limite_total ? `limite ${fmt(c.limite_total)}` : null,
              c.tem_cpf && 'CPF no cofre',
              c.cobranca_resumo && `cobrança ${c.cobranca_resumo}`].filter(Boolean).join(' · ') || '—'}
          </div>
        </td>
        <td style={{ ...tdS, fontFamily: 'monospace', fontSize: 14 }}>
          {revelado ? (
            <div style={{ color: colors.warning }}>
              <div>{fmtNum(revelado.numero)} <button onClick={() => copiar(revelado.numero)} title="Copiar número" style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.info }}><Copy size={12} /></button></div>
              <div style={{ fontSize: 12 }}>val {revelado.validade} · cvv {revelado.cvv} <button onClick={() => copiar(revelado.cvv)} title="Copiar CVV" style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.info }}><Copy size={12} /></button>{revelado.nome_impresso ? ` · ${revelado.nome_impresso}` : ''}</div>
              {/* Dados que o checkout pede além do cartão. */}
              {(revelado.cpf_titular_cartao || revelado.cobranca?.logradouro || revelado.cobranca?.cep) && (
                <div style={{ fontSize: 11, color: colors.textMuted, fontFamily: 'inherit', marginTop: 4 }}>
                  {revelado.cpf_titular_cartao && <div>CPF {revelado.cpf_titular_cartao} <button onClick={() => copiar(revelado.cpf_titular_cartao)} title="Copiar CPF" style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.info }}><Copy size={11} /></button></div>}
                  {(() => {
                    const e = revelado.cobranca || {};
                    const txt = [e.logradouro, e.numero, e.complemento, e.bairro, e.cidade, e.uf, e.cep].filter(Boolean).join(', ');
                    return txt ? <div>Cobrança: {txt} <button onClick={() => copiar(txt)} title="Copiar endereço" style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.info }}><Copy size={11} /></button></div> : null;
                  })()}
                </div>
              )}
            </div>
          ) : (c.tem_dados ? <span>{c.mascara}{c.nome_impresso ? <span style={{ fontSize: 11, color: colors.textDim, fontFamily: 'inherit' }}> · {c.nome_impresso}</span> : null}</span> : <span style={{ color: colors.textDim, fontFamily: 'inherit', fontSize: 12 }}>sem dados no cofre</span>)}
        </td>
        <td style={{ ...tdS, fontSize: 11, color: colors.textDim, whiteSpace: 'nowrap' }}>{c.atualizado_em ? `${c.atualizado_por || '—'} · ${new Date(c.atualizado_em).toLocaleDateString('pt-BR')}` : '—'}</td>
        <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
          {c.tem_dados && (revelado
            ? <Button size="sm" variant="outline" icon={EyeOff} onClick={() => setRevelado(null)}>Ocultar</Button>
            : <Button size="sm" variant="outline" icon={Eye} color={colors.warning} onClick={revelar} disabled={busy}>Revelar</Button>)}
          {podeEditar && <span style={{ marginLeft: 6 }}><Button size="sm" variant="outline" icon={Save} onClick={() => setEditando(v => !v)}>{c.tem_dados ? 'Substituir' : 'Guardar'}</Button></span>}
          {podeEditar && c.tem_dados && <span style={{ marginLeft: 6 }}><Button size="sm" variant="outline" icon={Trash2} color={colors.danger} onClick={apagar}>Apagar</Button></span>}
        </td>
      </tr>
      {editando && (
        <tr style={{ background: `${colors.warning}08` }}>
          <td colSpan={4} style={{ padding: '10px 14px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2, minmax(0, 1fr))' : '2fr 1fr 1fr 2fr auto', gap: 10, alignItems: 'end' }}>
              <div style={{ gridColumn: mobile ? '1 / -1' : undefined }}><label style={{ fontSize: 11, color: colors.textMuted }}>Número do cartão</label><input style={inp} inputMode="numeric" autoComplete="off" value={form.numero} onChange={e => setForm(f => ({ ...f, numero: e.target.value }))} placeholder="0000 0000 0000 0000" /></div>
              <div><label style={{ fontSize: 11, color: colors.textMuted }}>Validade</label><input style={inp} autoComplete="off" value={form.validade} onChange={e => setForm(f => ({ ...f, validade: e.target.value }))} placeholder="MM/AA" /></div>
              <div><label style={{ fontSize: 11, color: colors.textMuted }}>CVV</label><input style={inp} type="password" autoComplete="off" value={form.cvv} onChange={e => setForm(f => ({ ...f, cvv: e.target.value }))} placeholder="•••" /></div>
              <div style={{ gridColumn: mobile ? '1 / -1' : undefined }}><label style={{ fontSize: 11, color: colors.textMuted }}>Nome impresso</label><input style={inp} value={form.nome_impresso} onChange={e => setForm(f => ({ ...f, nome_impresso: e.target.value }))} /></div>
              {!mobile && <div />}
            </div>
            {/* Compra online pede CPF e endereço de cobrança e recusa quando
                não batem com o cadastro do banco — por isso ficam junto. */}
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'minmax(0, 1fr)' : '1.4fr 2fr', gap: mobile ? 4 : 10, alignItems: 'center', marginTop: 10 }}>
              <div>
                <label style={{ fontSize: 11, color: colors.textMuted }}>CPF do titular do cartão</label>
                <input style={{ ...inp, opacity: form.mesmo_cpf ? 0.6 : 1 }} disabled={form.mesmo_cpf} autoComplete="off"
                  placeholder={form.mesmo_cpf ? 'usando o CPF do titular' : 'somente números'}
                  value={form.mesmo_cpf ? '' : form.cpf_titular_cartao}
                  onChange={e => setForm(f => ({ ...f, cpf_titular_cartao: e.target.value.replace(/\D/g, '').slice(0, 11) }))} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.text, cursor: 'pointer', paddingTop: 14 }}>
                <input type="checkbox" checked={form.mesmo_cpf} onChange={e => setForm(f => ({ ...f, mesmo_cpf: e.target.checked }))} />
                Cartão no CPF do titular (repetir do cadastro)
              </label>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.text, cursor: 'pointer', marginTop: 8 }}>
              <input type="checkbox" checked={form.mesmo_endereco} onChange={e => setForm(f => ({ ...f, mesmo_endereco: e.target.checked }))} />
              Endereço de cobrança é o do cadastro do titular
            </label>
            {!form.mesmo_endereco && (
              <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2, minmax(0, 1fr))' : '1fr 2.5fr 0.8fr 1fr 1.5fr 1.5fr 0.5fr', gap: 8, marginTop: 8 }}>
                {[['cobranca_cep', 'CEP'], ['cobranca_logradouro', 'Logradouro'], ['cobranca_numero', 'Nº'], ['cobranca_complemento', 'Compl.'],
                  ['cobranca_bairro', 'Bairro'], ['cobranca_cidade', 'Cidade'], ['cobranca_uf', 'UF']].map(([k, rot]) => (
                    <div key={k} style={{ gridColumn: mobile && k === 'cobranca_logradouro' ? '1 / -1' : undefined }}>
                      <label style={{ fontSize: 11, color: colors.textMuted }}>{rot}</label>
                      <input style={inp} maxLength={k === 'cobranca_uf' ? 2 : undefined} value={form[k]}
                        onChange={e => setForm(f => ({ ...f, [k]: k === 'cobranca_uf' ? e.target.value.toUpperCase() : e.target.value }))} />
                    </div>
                  ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              <Button size="sm" color={colors.success} icon={Lock} onClick={salvar} disabled={busy}>Guardar no cofre</Button>
              <Button size="sm" variant="outline" onClick={() => setEditando(false)}>Cancelar</Button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function CofrePage() {
  const [data, setData] = useState(null);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [acessos, setAcessos] = useState(null);
  const carregar = useCallback(async () => {
    try { setData(await api('/cartoes')); setErro(''); } catch (e) { setErro(e.message); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);
  const verAcessos = async () => {
    if (acessos) { setAcessos(null); return; }
    try { setAcessos((await api('/acessos?limite=300')).acessos || []); } catch (e) { alert(e.message); }
  };
  const lista = useMemo(() => {
    const b = busca.toLowerCase().trim();
    return (data?.cartoes || []).filter(c => !b || [c.nome_cartao, c.titular_nome, c.banco, c.final].filter(Boolean).join(' ').toLowerCase().includes(b));
  }, [data, busca]);
  if (erro && !data) return <div style={{ padding: 20, color: colors.danger }}>{erro}</div>;
  if (!data) return <LoadingSpinner />;
  const gestao = data.nivel === 'GESTAO';
  return (
    <div>
      <PageHeader title="Cofre de cartões" subtitle="Número, validade e CVV cifrados no servidor · cada consulta fica registrada com login, IP e hora" />
      {!data.configurado && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 14, borderRadius: 10, marginBottom: 14, background: `${colors.danger}12`, border: `1px solid ${colors.danger}55`, color: colors.text, fontSize: 13 }}>
          <ShieldAlert size={18} color={colors.danger} />
          <div><b>Cofre desligado.</b> Falta a variável <code>COFRE_CHAVE</code> no ambiente do servidor (Railway → Variables). Sem ela nada é gravado nem lido — de propósito.</div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input style={{ ...inp, width: 320, maxWidth: '100%' }} value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar cartão, titular, banco, final…" />
        <Badge text={gestao ? 'ACESSO DE GESTÃO' : 'SEUS CARTÕES'} color={gestao ? colors.info : colors.success} />
        <span style={{ flex: 1 }} />
        {gestao && <Button size="sm" variant="outline" icon={History} onClick={verAcessos}>{acessos ? 'Ocultar trilha' : 'Trilha de acessos'}</Button>}
      </div>
      {acessos && (
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, marginBottom: 14, maxHeight: 300, overflowY: 'auto' }}>
          {acessos.length === 0 ? <EmptyState message="Nenhum acesso registrado" /> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: `1px solid ${colors.border}` }}><th style={thS}>Quando</th><th style={thS}>Quem</th><th style={thS}>Ação</th><th style={thS}>Cartão</th><th style={thS}>IP</th></tr></thead>
              <tbody>{acessos.map(a => (
                <tr key={a.id} style={{ borderBottom: `1px solid ${colors.border}22` }}>
                  <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{new Date(a.em).toLocaleString('pt-BR')}</td>
                  <td style={tdS}>{a.usuario_login}</td>
                  <td style={tdS}><Badge text={a.acao} color={a.acao === 'VER' ? colors.warning : a.acao === 'APAGAR' ? colors.danger : colors.info} /></td>
                  <td style={tdS}>{a.nome_cartao || `#${a.cartao_id}`}<div style={{ fontSize: 10, color: colors.textDim }}>{a.titular_nome || ''}</div></td>
                  <td style={{ ...tdS, fontSize: 11, color: colors.textDim }}>{a.ip || '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}
      <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {lista.length === 0 ? <EmptyState message="Nenhum cartão" /> : (
          // xm-empilha: no celular cada linha vira um bloco, célula embaixo de célula.
          <table className="xm-empilha" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${colors.border}` }}><th style={thS}>Cartão</th><th style={thS}>Dados</th><th style={thS}>Atualizado</th><th style={thS}>Ações</th></tr></thead>
            <tbody>{lista.map(c => <LinhaCartao key={c.id} c={c} podeEditar={data.configurado} onChange={carregar} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
